import React from 'react';
import styled from 'styled-components';

const CardContainer = styled.div`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(8px);
  
  &:hover {
    box-shadow: 0 8px 32px rgba(168, 85, 247, 0.2);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 16px;
  }
`;

const TokenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TokenLogo = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  object-fit: cover;
  border: none;
  background: transparent;
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const TokenInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TokenName = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const TokenSymbol = styled.div<{ $color?: string }>`
  font-size: 15px;
  color: ${props => props.$color || '#a855f7'};
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const StatBox = styled.div`
  background: transparent;
  border-radius: 10px;
  padding: 14px;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const StatValue = styled.div`
  font-size: 16px;
  color: #ffffff;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const APYRow = styled.div<{ $color?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
  }
`;

const APYLabel = styled.div`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const APYValue = styled.div<{ $color?: string }>`
  font-size: 16px;
  color: ${props => props.$color || '#a855f7'};
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ActionButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 10px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 16px;
  background: ${props => 
    props.$variant === 'danger' ? 'linear-gradient(135deg, #ea580c, #c2410c)' :
    props.$variant === 'secondary' ? 'linear-gradient(135deg, #10b981, #059669)' :
    'linear-gradient(135deg, #a855f7, #7c3aed)'
  };
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px ${props => 
      props.$variant === 'danger' ? 'rgba(234, 88, 12, 0.4)' :
      props.$variant === 'secondary' ? 'rgba(16, 185, 129, 0.4)' :
      'rgba(168, 85, 247, 0.4)'
    };
  }
  
  &:disabled {
    background: rgba(80, 80, 90, 0.4);
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 12px;
  }
`;

interface AssetCardProps {
  assetSymbol: string;
  assetName: string;
  logo: string;
  logoFallback?: string;
  balance: string;
  aTokenBalance: string;
  aTokenSymbol: string;
  supplyAPY: string;
  borrowAPY: string;
  tydroInkPoints: string; // Points per $1k supplied daily
  symbolColor?: string;
  apyColor?: string;
  onSupply: () => void;
  onBorrow: () => void;
  onManage: () => void;
  onTrade: () => void;
  hasCollateral: boolean;
  hasDebt: boolean;
  hasATokenBalance: boolean;
}

const AssetCard: React.FC<AssetCardProps> = ({
  assetSymbol,
  assetName,
  logo,
  logoFallback,
  balance,
  aTokenBalance,
  aTokenSymbol,
  supplyAPY,
  borrowAPY,
  tydroInkPoints,
  symbolColor,
  apyColor,
  onSupply,
  onBorrow,
  onManage,
  onTrade,
  hasCollateral,
  hasDebt,
  hasATokenBalance
}) => {
  // Format APY to show <0.01% for very small values
  const formatAPY = (apy: string) => {
    const apyNum = parseFloat(apy);
    if (apyNum === 0) return '0.00';
    if (apyNum < 0.01) return '<0.01';
    return apyNum.toFixed(2);
  };

  return (
    <CardContainer>
      <TokenHeader>
        <TokenLogo 
          src={logo} 
          alt={assetSymbol}
          onError={(e) => {
            if (logoFallback) {
              e.currentTarget.src = logoFallback;
            }
          }}
        />
        <TokenInfo>
          <TokenName>{assetName}</TokenName>
          <TokenSymbol $color={symbolColor}>{assetSymbol}</TokenSymbol>
        </TokenInfo>
      </TokenHeader>

      <StatsGrid>
        <StatBox>
          <StatLabel>Wallet Balance</StatLabel>
          <StatValue>{balance} {assetSymbol}</StatValue>
        </StatBox>
        <StatBox>
          <StatLabel>Supplied ({assetSymbol})</StatLabel>
          <StatValue>{aTokenBalance} {assetSymbol}</StatValue>
        </StatBox>
      </StatsGrid>

      <APYRow $color="#9333ea">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img 
            src="/tydroink.svg" 
            alt="TydroInk Points"
            title={`Amount of TydroInk Points earned daily per $1k of ${assetSymbol} supplied to lending pools.`}
            style={{ 
              width: '20px', 
              height: '20px',
              cursor: 'help'
            }}
          />
          <APYValue $color="#9333ea">{tydroInkPoints}</APYValue>
        </div>
      </APYRow>

      <APYRow $color={apyColor}>
        <APYLabel>Supply / Borrow APY:</APYLabel>
        <APYValue $color={apyColor}>{formatAPY(supplyAPY)}% / {formatAPY(borrowAPY)}%</APYValue>
      </APYRow>

      <ActionButtonRow>
        <ActionButton onClick={onSupply}>
          Supply
        </ActionButton>
        <ActionButton 
          $variant="secondary"
          onClick={onBorrow}
          disabled={!hasCollateral}
        >
          Borrow
        </ActionButton>
        <ActionButton 
          $variant="danger"
          onClick={onManage}
          disabled={!hasDebt && !hasATokenBalance}
        >
          Manage
        </ActionButton>
        <ActionButton onClick={onTrade}>
          Trade
        </ActionButton>
      </ActionButtonRow>
    </CardContainer>
  );
};

export default AssetCard;

